import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Newspaper,
  Calendar,
  FileText,
  MapPin,
  Layers,
  Trash2,
  Recycle,
  Settings,
  Bell,
  Phone,
  Menu,
  X,
  LogOut,
  Users,
  AlertCircle,
  Layout as LayoutIcon,
  ScrollText,
  Clock,
  Eye,
  MessageCircle,
  Siren,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getEffectiveAllowedRoutes } from '../config/panelRoutes';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { storagePublicBaseUrl } from '../config/env';
import { supabaseAdmin } from '../config/supabase';
import {
  ALARM_SYSTEMS_SECTION_TITLE_DEFAULT,
  ALARM_SYSTEMS_SECTION_TITLE_EVENT,
  ALARM_SYSTEMS_SECTION_TITLE_KEY,
  normalizeAlarmSystemsSectionTitle,
} from '../constants/alarmSystems';
import './Layout.css';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minut

function formatCountdown(seconds) {
  if (seconds == null || seconds < 0) return '30:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const heroBackgroundUrl = storagePublicBaseUrl
  ? `${storagePublicBaseUrl}/storage/v1/object/public/app-assets/startscreen-background/hero-background1.png`
  : null;
const appIconUrl = storagePublicBaseUrl
  ? `${storagePublicBaseUrl}/storage/v1/object/public/appicon/pins1.png`
  : null;
const mainHeaderIconUrl = storagePublicBaseUrl
  ? `${storagePublicBaseUrl}/storage/v1/object/public/appicon/pins1.png`
  : null;

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [alarmSystemsLabel, setAlarmSystemsLabel] = useState(ALARM_SYSTEMS_SECTION_TITLE_DEFAULT);
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { secondsRemaining } = useInactivityLogout(INACTIVITY_TIMEOUT_MS, () => {
    logout();
    navigate('/login');
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabaseAdmin
          .from('app_settings')
          .select('setting_value')
          .eq('setting_key', ALARM_SYSTEMS_SECTION_TITLE_KEY)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) setAlarmSystemsLabel(normalizeAlarmSystemsSectionTitle(data?.setting_value));
      } catch (error) {
        console.error(error);
      }
    })();
    const onTitleChanged = (event) => {
      setAlarmSystemsLabel(normalizeAlarmSystemsSectionTitle(event?.detail));
    };
    window.addEventListener(ALARM_SYSTEMS_SECTION_TITLE_EVENT, onTitleChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(ALARM_SYSTEMS_SECTION_TITLE_EVENT, onTitleChanged);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Wszystkie dostępne zakładki
  const allMenuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin'] },
    { path: '/news', icon: Newspaper, label: 'Aktualności', roles: ['admin', 'moderator'] },
    { path: '/events', icon: Calendar, label: 'Wydarzenia', roles: ['admin', 'moderator'] },
    { path: '/reports', icon: FileText, label: 'Zgłoszenia', roles: ['admin', 'support', 'secretary'] },
    { path: '/interactive-map/places', icon: MapPin, label: 'Mapa - Miejsca', roles: ['admin'] },
    { path: '/interactive-map/categories', icon: Layers, label: 'Mapa - Kategorie', roles: ['admin'] },
    { path: '/waste-schedule', icon: Trash2, label: 'Harmonogram', roles: ['admin'] },
    { path: '/segregacja', icon: Recycle, label: 'Segregacja', roles: ['admin'] },
    { path: '/contact', icon: Phone, label: 'Kontakt', roles: ['admin'] },
    { path: '/alarm-systems', icon: Siren, label: alarmSystemsLabel, roles: ['admin'] },
    { path: '/chat-assistant', icon: MessageCircle, label: 'Asystent (chat)', roles: ['admin'] },
    { path: '/app-settings', icon: Settings, label: 'Wygląd', roles: ['admin'] },
    { path: '/push-notifications', icon: Bell, label: 'Powiadomienia', roles: ['admin', 'secretary'] },
    { path: '/manage', icon: LayoutIcon, label: 'Zarządzanie', roles: ['admin'] },
    { path: '/users', icon: Users, label: 'Użytkownicy', roles: ['admin'] },
    { path: '/status-requests', icon: AlertCircle, label: 'Prośby o zmianę statusu', roles: ['admin'] },
    { path: '/audit-log', icon: ScrollText, label: 'Dziennik zdarzeń', roles: ['admin', 'moderator', 'support', 'secretary'] },
    { path: '/visit-counts', icon: Eye, label: 'Liczba odwiedzin', roles: ['admin'] },
  ];

  const menuItems = allMenuItems.filter((item) => {
    if (!user?.role) return false;
    if (user.role === 'admin') return item.roles.includes('admin');
    if (Array.isArray(user.panel_allowed_routes)) {
      return getEffectiveAllowedRoutes(user).includes(item.path);
    }
    return item.roles.includes(user.role);
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="admin-layout">
      {heroBackgroundUrl && (
        <div
          className="admin-layout-bg"
          style={{ backgroundImage: `url(${heroBackgroundUrl})` }}
          aria-hidden="true"
        />
      )}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            {appIconUrl && (
              <img src={appIconUrl} alt="" className="sidebar-logo-img" />
            )}
            <h2>Biel<span className="brand-accent">appka</span></h2>
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <Icon size={20} />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {sidebarOpen && user && (
            <div className="user-info">
              <p className="user-name">{user.name || user.email}</p>
              <p className="user-email">{user.email}</p>
              <p className="user-role">
                {user.role === 'admin' ? 'Administrator' : 
                 user.role === 'moderator' ? 'Moderator' : 
                 user.role === 'support' ? 'Obsługa' : 
                 user.role === 'secretary' ? 'Sekretarz' : user.role}
              </p>
            </div>
          )}
          <button 
            className="logout-button"
            onClick={handleLogout}
            title="Wyloguj się"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Wyloguj</span>}
          </button>
        </div>
      </div>
      <div className="main-content">
        <header className="main-header">
          <div className="main-header-left">
            {mainHeaderIconUrl && (
              <img src={mainHeaderIconUrl} alt="" className="main-header-logo" />
            )}
            <h1>Biel<span className="brand-accent">appka</span></h1>
          </div>
          <div className="inactivity-countdown" title="Wylogowanie po 30 min bezczynności">
            <Clock size={18} aria-hidden="true" />
            <span className="inactivity-countdown-label">Wylogowanie za:</span>
            <span className="inactivity-countdown-time">{formatCountdown(secondsRemaining)}</span>
          </div>
        </header>
        <div className="content-wrapper">
          {children}
        </div>
      </div>
    </div>
  );
}

